import {
  MapPin,
  Building2,
  Save,
  AlertCircle,
  Check,
  Globe,
  RefreshCw,
  Map,
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  Star,
  Phone,
  Mail,
  Warehouse,
  Factory,
  Store,
  Home,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { komunikatZapisuLokalizacji } from '../../lib/limitLokalizacji';
import { SHELL_EVENTS, publishShellEvent } from '../../lib/shellEvents';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// Site types with icons
const SITE_TYPES = [
  { value: 'headquarters', label: 'Siedziba główna', icon: Building2, color: 'text-blue-400' },
  { value: 'branch', label: 'Oddział', icon: Store, color: 'text-green-400' },
  { value: 'warehouse', label: 'Magazyn', icon: Warehouse, color: 'text-yellow-400' },
  { value: 'production', label: 'Zakład produkcyjny', icon: Factory, color: 'text-orange-400' },
  { value: 'office', label: 'Biuro', icon: Home, color: 'text-purple-400' },
];

interface OrganizationSite {
  id: string;
  organization_id: string;
  name: string;
  site_type: string;
  is_primary: boolean;
  address: string | null;
  city: string;
  postal_code: string | null;
  voivodeship: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  email: string | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

interface SiteFormData {
  name: string;
  site_type: string;
  is_primary: boolean;
  address: string;
  city: string;
  postal_code: string;
  voivodeship: string;
  country: string;
  latitude: number | undefined;
  longitude: number | undefined;
  phone: string;
  email: string;
  description: string;
}

const initialFormData: SiteFormData = {
  name: '',
  site_type: 'branch',
  is_primary: false,
  address: '',
  city: '',
  postal_code: '',
  voivodeship: '',
  country: 'Polska',
  latitude: undefined,
  longitude: undefined,
  phone: '',
  email: '',
  description: '',
};

// Normalize Polish address by removing/expanding common prefixes
const normalizePolishAddress = (address: string): string => {
  if (!address) return address;

  const prefixPatterns = [
    /^ul\.\s*/i,
    /^ulica\s+/i,
    /^al\.\s*/i,
    /^aleja\s+/i,
    /^os\.\s*/i,
    /^osiedle\s+/i,
    /^pl\.\s*/i,
    /^plac\s+/i,
    /^rondo\s+/i,
    /^skwer\s+/i,
    /^bulwar\s+/i,
    /^wybrzeże\s+/i,
    /^park\s+/i,
  ];

  let normalized = address.trim();
  for (const pattern of prefixPatterns) {
    normalized = normalized.replace(pattern, '');
  }
  return normalized.trim();
};

// Polish cities coordinates lookup for fallback
const POLISH_CITIES: { [key: string]: { lat: number; lon: number } } = {
  warszawa: { lat: 52.2297, lon: 21.0122 },
  kraków: { lat: 50.0647, lon: 19.945 },
  gdańsk: { lat: 54.352, lon: 18.6466 },
  gdynia: { lat: 54.5189, lon: 18.5305 },
  poznań: { lat: 52.4064, lon: 16.9252 },
  wrocław: { lat: 51.1079, lon: 17.0385 },
  łódź: { lat: 51.7592, lon: 19.456 },
  szczecin: { lat: 53.4285, lon: 14.5528 },
  lublin: { lat: 51.2465, lon: 22.5684 },
  katowice: { lat: 50.2649, lon: 19.0238 },
  bydgoszcz: { lat: 53.1235, lon: 18.0084 },
  białystok: { lat: 53.1325, lon: 23.1688 },
  toruń: { lat: 53.0138, lon: 18.5984 },
  rzeszów: { lat: 50.0412, lon: 21.9991 },
  kielce: { lat: 50.8661, lon: 20.6286 },
  olsztyn: { lat: 53.7784, lon: 20.4801 },
  opole: { lat: 50.6751, lon: 17.9213 },
};

export function SitesPage() {
  const { t } = useTranslation();
  const { currentOrganization, hasRole } = useAuth();
  const [sites, setSites] = useState<OrganizationSite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<OrganizationSite | null>(null);
  const [formData, setFormData] = useState<SiteFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canEditSettings = hasRole(['owner', 'admin']);

  useEffect(() => {
    if (currentOrganization) {
      fetchSites();
    }
  }, [currentOrganization]);

  const fetchSites = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('organization_sites')
        .select('*')
        .eq('organization_id', currentOrganization.organization_id)
        .order('is_primary', { ascending: false })
        .order('name');

      if (error) throw error;
      setSites(data || []);
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError(t('sites.loadError', 'Nie udało się wczytać lokalizacji'));
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingSite(null);
    setFormData({
      ...initialFormData,
      is_primary: sites.length === 0, // First site is primary by default
    });
    setFormError(null);
    setGeocodeError(null);
    setShowModal(true);
  };

  const openEditModal = (site: OrganizationSite) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      site_type: site.site_type,
      is_primary: site.is_primary,
      address: site.address || '',
      city: site.city,
      postal_code: site.postal_code || '',
      voivodeship: site.voivodeship || '',
      country: site.country,
      latitude: site.latitude ?? undefined,
      longitude: site.longitude ?? undefined,
      phone: site.phone || '',
      email: site.email || '',
      description: site.description || '',
    });
    setFormError(null);
    setGeocodeError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSite(null);
    setFormData(initialFormData);
    setFormError(null);
    setGeocodeError(null);
  };

  // Geocode address using Nominatim API with structured search
  const geocodeAddress = useCallback(async () => {
    if (!formData.city) {
      setGeocodeError(t('sites.enterCity', 'Wprowadź miasto'));
      return;
    }

    setIsGeocoding(true);
    setGeocodeError(null);

    const tryGeocode = async (url: string): Promise<{ lat: number; lon: number } | null> => {
      try {
        const response = await fetch(url, { headers: { 'Accept-Language': 'pl' } });
        if (!response.ok) return null;
        const data = await response.json();
        if (data && data.length > 0) {
          return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
        }
        return null;
      } catch {
        return null;
      }
    };

    try {
      let result: { lat: number; lon: number } | null = null;

      // Strategy 1: Try structured search with street, city, postalcode
      if (formData.address) {
        const street = encodeURIComponent(normalizePolishAddress(formData.address));
        const city = encodeURIComponent(formData.city);
        const postalcode = formData.postal_code ? encodeURIComponent(formData.postal_code) : '';

        let url = `https://nominatim.openstreetmap.org/search?street=${street}&city=${city}&country=Poland&format=json&limit=1`;
        if (postalcode) url += `&postalcode=${postalcode}`;

        result = await tryGeocode(url);
      }

      // Strategy 2: Try free-form query with all parts
      if (!result) {
        const parts: string[] = [];
        if (formData.address) parts.push(normalizePolishAddress(formData.address));
        parts.push(formData.city);
        if (formData.postal_code) parts.push(formData.postal_code);
        parts.push('Polska');

        const query = encodeURIComponent(parts.join(', '));
        result = await tryGeocode(
          `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=pl`
        );
      }

      // Strategy 3: Try with just city and postal code
      if (!result && formData.postal_code) {
        const query = encodeURIComponent(`${formData.city}, ${formData.postal_code}, Polska`);
        result = await tryGeocode(
          `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=pl`
        );
      }

      // Strategy 4: Try with just city
      if (!result) {
        const query = encodeURIComponent(`${formData.city}, Polska`);
        result = await tryGeocode(
          `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=pl`
        );
      }

      // Strategy 5: Use hardcoded Polish cities as fallback
      if (!result) {
        const cityKey = formData.city.toLowerCase().trim();
        if (POLISH_CITIES[cityKey]) {
          result = { lat: POLISH_CITIES[cityKey].lat, lon: POLISH_CITIES[cityKey].lon };
        }
      }

      if (result) {
        setFormData((prev) => ({ ...prev, latitude: result!.lat, longitude: result!.lon }));
        setSuccess(t('sites.geocodeSuccess', 'Współrzędne zostały znalezione'));
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setGeocodeError(t('sites.geocodeNotFound', 'Nie znaleziono lokalizacji'));
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setGeocodeError(t('sites.geocodeError', 'Błąd wyszukiwania lokalizacji'));
    } finally {
      setIsGeocoding(false);
    }
  }, [formData.address, formData.city, formData.postal_code, t]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization || !canEditSettings) return;

    // Validation
    if (!formData.name.trim()) {
      setFormError('Nazwa lokalizacji jest wymagana');
      return;
    }
    if (!formData.city.trim()) {
      setFormError('Miasto jest wymagane');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const siteData = {
        organization_id: currentOrganization.organization_id,
        name: formData.name.trim(),
        site_type: formData.site_type,
        is_primary: formData.is_primary,
        address: formData.address.trim() || null,
        city: formData.city.trim(),
        postal_code: formData.postal_code.trim() || null,
        voivodeship: formData.voivodeship || null,
        country: formData.country,
        latitude: formData.latitude ?? null,
        longitude: formData.longitude ?? null,
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        description: formData.description.trim() || null,
      };

      if (editingSite) {
        const { error } = await supabase
          .from('organization_sites')
          .update(siteData)
          .eq('id', editingSite.id);

        if (error) throw error;
        void publishShellEvent(SHELL_EVENTS.siteUpdated, {
          site_id: editingSite.id,
          name: siteData.name,
          site_type: siteData.site_type,
          city: siteData.city,
          is_primary: siteData.is_primary,
        });
        setSuccess('Lokalizacja została zaktualizowana');
      } else {
        const { error } = await supabase.from('organization_sites').insert([siteData]);
        if (error) throw error;
        // No site_id: the insert deliberately stays a bare write (no `.select()`
        // round-trip that RLS could turn into a false failure), so the event
        // carries the site's natural identity instead.
        void publishShellEvent(SHELL_EVENTS.siteCreated, {
          name: siteData.name,
          site_type: siteData.site_type,
          city: siteData.city,
          is_primary: siteData.is_primary,
        });
        setSuccess('Nowa lokalizacja została dodana');
      }

      await fetchSites();
      closeModal();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving site:', err);
      // Limit planu egzekwuje baza; jej zdanie (liczba zajętych i limit) jest
      // jedyną prawdą, więc pokazujemy je zamiast ogólnego komunikatu.
      setFormError(komunikatZapisuLokalizacji(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (siteId: string) => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.from('organization_sites').delete().eq('id', siteId);
      if (error) throw error;

      void publishShellEvent(SHELL_EVENTS.siteDeleted, { site_id: siteId });
      await fetchSites();
      setShowDeleteConfirm(null);
      setSuccess('Lokalizacja została usunięta');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting site:', err);
      setError('Nie udało się usunąć lokalizacji');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetPrimary = async (site: OrganizationSite) => {
    if (site.is_primary) return;

    try {
      const { error } = await supabase
        .from('organization_sites')
        .update({ is_primary: true })
        .eq('id', site.id);

      if (error) throw error;

      void publishShellEvent(SHELL_EVENTS.siteUpdated, {
        site_id: site.id,
        name: site.name,
        site_type: site.site_type,
        city: site.city,
        is_primary: true,
      });
      await fetchSites();
      setSuccess('Lokalizacja główna została zmieniona');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error setting primary site:', err);
      setError('Nie udało się zmienić lokalizacji głównej');
    }
  };

  const getSiteTypeInfo = (type: string) => {
    return SITE_TYPES.find((t) => t.value === type) || SITE_TYPES[1];
  };

  // Get map URL for preview
  const getMapUrl = (lat: number | null, lon: number | null) => {
    if (lat && lon) {
      return `https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.01},${lat - 0.01},${lon + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lon}`;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-aurora-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <MapPin className="w-6 h-6 text-aurora-brand" />
            {t('sites.title', 'Lokalizacje')}
          </h1>
          <p className="pagehead__sub">
            {t('sites.subtitle', 'Zarządzaj lokalizacjami organizacji na mapie')}
          </p>
        </div>
        {canEditSettings && (
          <div className="pagehead__actions">
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj lokalizację
            </button>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/20 border border-red-500/30 rounded-ids-md text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/20 border border-green-500/30 rounded-ids-md text-green-400">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Sites List */}
      {sites.length === 0 ? (
        <div className="card p-8 text-center">
          <MapPin className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
          <h3 className="display text-lg font-medium text-aurora-text mb-2">Brak lokalizacji</h3>
          <p className="text-aurora-muted mb-4">Dodaj pierwszą lokalizację swojej organizacji</p>
          {canEditSettings && (
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj lokalizację
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => {
            const typeInfo = getSiteTypeInfo(site.site_type);
            const TypeIcon = typeInfo.icon;
            const mapUrl = getMapUrl(site.latitude, site.longitude);

            return (
              <div
                key={site.id}
                className={`card card--interactive ${
                  site.is_primary ? 'border-aurora-brand' : ''
                } overflow-hidden`}
              >
                {/* Map Preview */}
                <div className="h-32 bg-aurora-bg-subtle">
                  {mapUrl ? (
                    <iframe
                      src={mapUrl}
                      className="w-full h-full border-0"
                      title={`Map of ${site.name}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-aurora-subtle">
                      <Map className="w-8 h-8" />
                    </div>
                  )}
                </div>

                {/* Site Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                      <h3 className="font-semibold text-aurora-text">{site.name}</h3>
                    </div>
                    {site.is_primary && (
                      <span className="badge badge--brand">
                        <Star className="w-3 h-3" />
                        Główna
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-aurora-muted mb-1">{typeInfo.label}</p>

                  <div className="space-y-1 text-sm text-aurora-muted mt-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {site.address && `${site.address}, `}
                        {site.city}
                        {site.postal_code && ` ${site.postal_code}`}
                      </span>
                    </div>
                    {site.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <span>{site.phone}</span>
                      </div>
                    )}
                    {site.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{site.email}</span>
                      </div>
                    )}
                    {site.latitude && site.longitude && (
                      <div className="flex items-center gap-2 text-aurora-subtle text-xs">
                        <Globe className="w-3 h-3" />
                        <span className="mono">
                          {site.latitude.toFixed(4)}, {site.longitude.toFixed(4)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {canEditSettings && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-aurora-border">
                      <div className="flex items-center gap-2">
                        {!site.is_primary && (
                          <button
                            onClick={() => handleSetPrimary(site)}
                            className="iconbtn w-8 h-8"
                            title="Ustaw jako główną"
                          >
                            <Star className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(site)}
                          className="iconbtn w-8 h-8"
                          title="Edytuj"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                      {!site.is_primary && (
                        <button
                          onClick={() => setShowDeleteConfirm(site.id)}
                          className="iconbtn w-8 h-8 hover:text-red-400 hover:bg-red-500/10"
                          title="Usuń"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Delete confirmation */}
                  {showDeleteConfirm === site.id && (
                    <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md">
                      <p className="text-sm text-red-400 mb-2">
                        Czy na pewno chcesz usunąć tę lokalizację?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDelete(site.id)}
                          disabled={isDeleting}
                          className="btn btn--danger btn--sm"
                        >
                          {isDeleting ? 'Usuwanie...' : 'Usuń'}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          className="btn btn--secondary btn--sm"
                        >
                          Anuluj
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-aurora-surface rounded-ids-xl shadow-elev-4 max-w-2xl w-full border border-aurora-border max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-aurora-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-ids-md flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-aurora-brand" />
                </div>
                <div>
                  <h2 className="display text-lg font-bold text-aurora-text">
                    {editingSite ? 'Edytuj lokalizację' : 'Dodaj nową lokalizację'}
                  </h2>
                  <p className="text-sm text-aurora-muted">
                    {editingSite ? editingSite.name : 'Wypełnij dane lokalizacji'}
                  </p>
                </div>
              </div>
              <button onClick={closeModal} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <form id="site-form" onSubmit={handleSave} className="space-y-6">
                {formError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-ids-md p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{formError}</p>
                  </div>
                )}

                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="overline flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-aurora-brand" />
                    Informacje podstawowe
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="field__label block mb-1.5">
                        Nazwa lokalizacji <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="np. Biuro Warszawa, Magazyn Kraków"
                        required
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="field__label block mb-1.5">Typ lokalizacji</label>
                      <select
                        value={formData.site_type}
                        onChange={(e) => setFormData({ ...formData, site_type: e.target.value })}
                        className="select"
                      >
                        {SITE_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_primary}
                          onChange={(e) =>
                            setFormData({ ...formData, is_primary: e.target.checked })
                          }
                          className="w-4 h-4 rounded border-aurora-border-strong text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                        />
                        <div>
                          <span className="text-aurora-text text-sm font-medium">
                            Lokalizacja główna
                          </span>
                          <p className="text-xs text-aurora-subtle">Wyświetlana jako pierwsza</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-4">
                  <h3 className="overline flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-400" />
                    Adres
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="field__label block mb-1.5">Ulica i numer</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            address: e.target.value,
                            latitude: undefined,
                            longitude: undefined,
                          })
                        }
                        placeholder="ul. Przykładowa 123"
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="field__label block mb-1.5">
                        Miasto <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            city: e.target.value,
                            latitude: undefined,
                            longitude: undefined,
                          })
                        }
                        placeholder="Warszawa"
                        required
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="field__label block mb-1.5">Kod pocztowy</label>
                      <input
                        type="text"
                        value={formData.postal_code}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            postal_code: e.target.value,
                            latitude: undefined,
                            longitude: undefined,
                          })
                        }
                        placeholder="00-001"
                        className="input"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="field__label block mb-1.5">Województwo</label>
                      <select
                        value={formData.voivodeship}
                        onChange={(e) => setFormData({ ...formData, voivodeship: e.target.value })}
                        className="select"
                      >
                        <option value="">Wybierz województwo</option>
                        <option value="dolnośląskie">dolnośląskie</option>
                        <option value="kujawsko-pomorskie">kujawsko-pomorskie</option>
                        <option value="lubelskie">lubelskie</option>
                        <option value="lubuskie">lubuskie</option>
                        <option value="łódzkie">łódzkie</option>
                        <option value="małopolskie">małopolskie</option>
                        <option value="mazowieckie">mazowieckie</option>
                        <option value="opolskie">opolskie</option>
                        <option value="podkarpackie">podkarpackie</option>
                        <option value="podlaskie">podlaskie</option>
                        <option value="pomorskie">pomorskie</option>
                        <option value="śląskie">śląskie</option>
                        <option value="świętokrzyskie">świętokrzyskie</option>
                        <option value="warmińsko-mazurskie">warmińsko-mazurskie</option>
                        <option value="wielkopolskie">wielkopolskie</option>
                        <option value="zachodniopomorskie">zachodniopomorskie</option>
                      </select>
                    </div>
                  </div>

                  {/* Geocode Button */}
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      onClick={geocodeAddress}
                      disabled={isGeocoding}
                      className="btn btn--tonal"
                    >
                      {isGeocoding ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                      {isGeocoding ? 'Wyszukuję...' : 'Wyszukaj współrzędne'}
                    </button>

                    {geocodeError && <span className="text-red-400 text-sm">{geocodeError}</span>}

                    {formData.latitude && formData.longitude && (
                      <span className="text-green-400 text-sm flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <h3 className="overline flex items-center gap-2">
                    <Phone className="w-4 h-4 text-yellow-400" />
                    Kontakt (opcjonalnie)
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="field__label block mb-1.5">Telefon</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+48 123 456 789"
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="field__label block mb-1.5">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="biuro@firma.pl"
                        className="input"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="field__label block mb-1.5">Opis / Uwagi</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Dodatkowe informacje o lokalizacji..."
                        rows={2}
                        className="textarea resize-none"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-aurora-border">
              <p className="text-xs text-aurora-subtle">
                <span className="text-red-400">*</span> Pola wymagane
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="btn btn--secondary">
                  Anuluj
                </button>
                <button
                  type="submit"
                  form="site-form"
                  disabled={isSaving}
                  className="btn btn--primary min-w-[120px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingSite ? 'Zapisz zmiany' : 'Dodaj lokalizację'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-ids-lg p-4">
        <div className="flex gap-3">
          <Globe className="w-5 h-5 text-aurora-brand flex-shrink-0 mt-0.5" />
          <div className="text-sm text-aurora-muted">
            <p className="font-medium text-aurora-text mb-1">Jak działają lokalizacje?</p>
            <p>
              Możesz dodać wiele lokalizacji dla swojej organizacji - oddziały, magazyny, biura.
              Lokalizacja główna jest wyświetlana na globie na stronie głównej. Wyszukaj współrzędne
              GPS automatycznie lub wprowadź je ręcznie.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SitesPage;
